import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

/**
 * API client for the Company Research service.
 *
 * Talks to the public API Gateway routes under `/research/*`. All calls
 * are JWT-authenticated via the standard token interceptors.
 */
const researchClient = axios.create({
  baseURL: config.api.researchServiceUrl,
  timeout: 600000, // chat + earnings extraction can take ~minutes
  headers: { 'Content-Type': 'application/json' },
});

researchClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('token');
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

applyAuthRetryInterceptor(researchClient);
applyTokenRefreshInterceptor(researchClient);

const researchService = {
  /** List all companies registered for research. */
  async listCompanies() {
    const { data } = await researchClient.get('/research/companies');
    return data.companies || [];
  },

  /** Add a new company. Returns the created company. */
  async createCompany({ name, ticker, notes }) {
    const { data } = await researchClient.post('/research/companies', { name, ticker, notes });
    return data;
  },

  /** List uploaded files for a company, newest first. */
  async listFiles(companyId) {
    const { data } = await researchClient.get('/research/files', {
      params: { company_id: companyId },
    });
    return data.files || [];
  },

  /** List every company-related S3 file across known source prefixes. */
  async listCompanyS3Files(companyId, { includeMetadataSidecars = false } = {}) {
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/s3-files`,
      {
        params: {
          include_metadata_sidecars: includeMetadataSidecars,
        },
      },
    );
    return data; // { company_id, bucket, prefixes, count, files }
  },

  /** Fetch structured earnings rows for a company. */
  async getEarnings(companyId) {
    const { data } = await researchClient.get(`/research/earnings/${encodeURIComponent(companyId)}`);
    return data.rows || [];
  },

  /** Return a sorted list of every known SEC XBRL tag name (for autocomplete). */
  async getKnownTags() {
    const { data } = await researchClient.get('/research/model-config/known-tags');
    return data; // string[]
  },

  /** Fetch the global financial model config (IS/BS/CFS row definitions). */
  async getGlobalModelConfig(definitionId) {
    const { data } = await researchClient.get('/research/model-config', {
      params: definitionId ? { definition_id: definitionId } : {},
    });
    return data;
  },

  /** Save global model config — all rows for all sheets, no overrides/custom split. */
  async saveGlobalModelConfig(savedRows, definitionId, sheets) {
    const body = { overrides: {}, saved_rows: savedRows, ...(sheets ? { sheets } : {}) };
    const { data } = await researchClient.put('/research/model-config', body, {
      params: definitionId ? { definition_id: definitionId } : {},
    });
    return data;
  },

  /** List all spreadsheet model definitions (default + custom). */
  async listModelDefinitions() {
    const { data } = await researchClient.get('/research/model-config/definitions');
    return data.definitions || [];
  },

  /** Create a new spreadsheet model definition seeded from `copyFrom` (default if omitted). */
  async createModelDefinition(name, copyFrom) {
    const { data } = await researchClient.post('/research/model-config/definitions', {
      name,
      ...(copyFrom ? { copy_from: copyFrom } : {}),
    });
    return data; // { id, name }
  },

  /** Delete a named definition.  The "default" definition cannot be deleted. */
  async deleteModelDefinition(definitionId) {
    const { data } = await researchClient.delete(
      `/research/model-config/definitions/${encodeURIComponent(definitionId)}`,
    );
    return data;
  },

  /** Rename a named definition. */
  async renameDefinition(definitionId, newName) {
    const { data } = await researchClient.post(
      `/research/model-config/definitions/${encodeURIComponent(definitionId)}/rename`,
      { name: newName },
    );
    return data;
  },

  /** Fetch the financial model config (IS/BS/CFS row definitions) for a company. */
  async getModelConfig(companyId) {
    const { data } = await researchClient.get(`/research/companies/${encodeURIComponent(companyId)}/model-config`);
    return data;
  },

  /**
   * Save per-company model config overrides.
   * @param {string} companyId
   * @param {Object} overrides  flat dict of { canonical_key → { label?, sec_tags?, negate? } }
   */
  async saveModelConfig(companyId, overrides) {
    const { data } = await researchClient.put(
      `/research/companies/${encodeURIComponent(companyId)}/model-config`,
      overrides,
    );
    return data;
  },

  /** Fetch the SEC XBRL tag → normalized column mapping config. */
  async getSecTagNormalization() {
    const { data } = await researchClient.get('/research/sec-tag-normalization');
    return data.config || {};
  },

  /** Save the SEC XBRL tag → normalized column mapping config. */
  async saveSecTagNormalization(normConfig) {
    const { data } = await researchClient.put('/research/sec-tag-normalization', { config: normConfig });
    return data;
  },

  /**
   * Upload a research file. Two-step presigned flow:
   *   1. POST /upload/presigned -> get S3 PUT url + file_id
   *   2. PUT bytes directly to S3
   *   3. POST /upload/confirm -> service tags object, writes KB metadata
   *      sidecar, runs the Earnings extractor (if applicable), and triggers
   *      a Bedrock KB ingestion job.
   *
   * @param {Object} params
   * @param {string} params.companyId
   * @param {File}   params.file
   * @param {'notes'|'earnings'} params.docType
   * @param {string=} params.author
   * @param {string=} params.documentDate     ISO 'YYYY-MM-DD'
   * @param {string=} params.title
   * @param {(percent:number)=>void=} params.onProgress
   */
  async uploadFile({ companyId, file, docType, author, documentDate, title, replaceFileId, onProgress }) {
    const presignBody = {
      company_id: companyId,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      doc_type: docType,
      author: author || undefined,
      document_date: documentDate || undefined,
      title: title || undefined,
    };

    const { data: presigned } = await researchClient.post('/research/upload/presigned', presignBody);
    if (!presigned.success || !presigned.upload_url) {
      throw new Error(presigned.error || 'Failed to get presigned URL');
    }

    await axios.put(presigned.upload_url, file, {
      headers: { 'Content-Type': presignBody.content_type },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
      // Don't add the JWT — S3 presigned URLs are pre-authorized.
      transformRequest: [(data) => data],
    });

    const { data: confirm } = await researchClient.post('/research/upload/confirm', {
      file_id: presigned.file_id,
      replace_file_id: replaceFileId || undefined,
    });
    return confirm;
  },

  /** Ask the research agent a question across one or both knowledge bases.
   *  Returns immediately with { query_id, status: "pending" }. */
  async startQuery({ companyId, question, sessionId, conversationId, kbScope, docTypes, fromDate, toDate }) {
    const { data } = await researchClient.post('/research/query', {
      company_id: companyId,
      question,
      session_id: sessionId || undefined,
      conversation_id: conversationId || undefined,
      kb_scope: kbScope || 'both',
      doc_types: docTypes || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    });
    return data; // { query_id, status: "pending" }
  },

  /** Poll query status until completed or failed.
   *  Returns the full ResearchQueryResponse when done. */
  async pollQuery(queryId) {
    const { data } = await researchClient.get(`/research/query/${encodeURIComponent(queryId)}/status`);
    return data; // { status: "pending" | "completed" | "failed", ...response }
  },

  /** List saved conversations for the selected company, newest first. */
  async listConversations(companyId) {
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/conversations`,
    );
    return data.conversations || [];
  },

  /** Load one saved conversation and its full message history. */
  async getConversation(companyId, conversationId) {
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/conversations/${encodeURIComponent(conversationId)}`,
    );
    return data; // { conversation, messages }
  },

  /** Rename a saved conversation. */
  async renameConversation(companyId, conversationId, title) {
    const { data } = await researchClient.put(
      `/research/companies/${encodeURIComponent(companyId)}/conversations/${encodeURIComponent(conversationId)}`,
      { title },
    );
    return data;
  },

  /** Delete a saved conversation. */
  async deleteConversation(companyId, conversationId) {
    const { data } = await researchClient.delete(
      `/research/companies/${encodeURIComponent(companyId)}/conversations/${encodeURIComponent(conversationId)}`,
    );
    return data;
  },

  /** Get a short-lived presigned download URL for a file. */
  async getDownloadUrl(fileId) {
    const { data } = await researchClient.get(`/research/files/${encodeURIComponent(fileId)}/download-url`);
    return data; // { url, filename }
  },

  /** Get a short-lived presigned URL for a Bedrock KB source by its S3 URI. */
  async presignSource(s3Uri) {
    const { data } = await researchClient.get('/research/presign-source', {
      params: { s3_uri: s3Uri },
    });
    return data; // { url, filename }
  },

  /**
   * List raw 10-Q/10-K filing documents stored in S3 for a company.
   * These live outside the KB prefix and are never ingested into the knowledge base.
   */
  async listSecFilings(companyId) {
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/sec-filings`,
    );
    return data; // { filings: [{s3_uri, filename, period_end_date, form_type, size_bytes, last_modified}] }
  },

  /** Overwrite the metrics dict for one earnings row. */
  async patchEarnings(companyId, periodEndDate, metrics) {
    const { data } = await researchClient.patch(
      `/research/earnings/${encodeURIComponent(companyId)}/${encodeURIComponent(periodEndDate)}`,
      { metrics },
    );
    return data;
  },

  /** Delete a file from the knowledge base. Optionally cascade-deletes linked earnings rows. */
  async deleteFile(fileId, cascadeEarnings = false) {
    const { data } = await researchClient.delete(
      `/research/files/${encodeURIComponent(fileId)}`,
      { params: { cascade_earnings: cascadeEarnings } },
    );
    return data;
  },

  /** Move a file into or out of KB-synced storage and trigger KB re-sync. */
  async setFileKbMembership(fileId, includeInKnowledgeBase) {
    const { data } = await researchClient.post(
      `/research/files/${encodeURIComponent(fileId)}/kb-membership`,
      { include_in_knowledge_base: !!includeInKnowledgeBase },
    );
    return data;
  },

  /** Permanently delete a company and all associated S3 files, DDB records, and Redshift data. */
  async deleteCompany(companyId) {
    const { data } = await researchClient.delete(
      `/research/companies/${encodeURIComponent(companyId)}`,
    );
    return data;
  },

  /** Delete a single structured earnings row. */
  async deleteEarnings(companyId, periodEndDate) {
    const { data } = await researchClient.delete(
      `/research/earnings/${encodeURIComponent(companyId)}/${encodeURIComponent(periodEndDate)}`,
    );
    return data;
  },

  async normalizeEarnings(companyId) {
    const { data } = await researchClient.post(
      `/research/earnings/${encodeURIComponent(companyId)}/normalize`,
    );
    return data;
  },

  /**
   * Start an async earnings extraction job for an already-uploaded file.
   * Returns { file_id, job_id, status: "pending" } immediately.
   */
  async startEarningsPreview(fileId, { force = false, docType = null, targetFiscalYear = null, targetFiscalQuarter = null, targetPeriodEndDate = null } = {}) {
    const body = { file_id: fileId, force };
    if (docType) body.doc_type = docType;
    if (targetFiscalYear) body.target_fiscal_year = targetFiscalYear;
    if (targetFiscalQuarter) body.target_fiscal_quarter = targetFiscalQuarter;
    if (targetPeriodEndDate) body.target_period_end_date = targetPeriodEndDate;
    const { data } = await researchClient.post('/research/upload/preview', body);
    return data;
  },

  /** Get a short-lived presigned download URL for a research file. */
  async getFileDownloadUrl(fileId) {
    const { data } = await researchClient.get(`/research/files/${encodeURIComponent(fileId)}/download-url`);
    return data; // { url, filename }
  },

  /**
   * Poll extraction job status for a file.
   * Returns { status: "pending" | "completed" | "failed", ...previewPayload? }
   */
  async pollEarningsPreview(fileId) {
    const { data } = await researchClient.get(`/research/upload/preview/${fileId}/status`);
    return data;
  },

  /**
   * Save user-confirmed earnings data after review.
   * @param {string} fileId
   * @param {Array}  confirmedPeriods  [{period_end_date, fiscal_year, fiscal_quarter,
   *                                    currency, is_primary, confirmed_metrics}]
   */
  async finalizeEarningsUpload(fileId, confirmedPeriods, docType = null) {
    const { data } = await researchClient.post('/research/upload/finalize', {
      file_id: fileId,
      confirmed_periods: confirmedPeriods,
      ...(docType ? { doc_type: docType } : {}),
    });
    return data;
  },

  /**
   * Use Claude Haiku to classify a recently-uploaded file by doc type.
   * @param {string} fileId
   * @returns {{ suggested_doc_type: string, reasoning: string }}
   */
  async classifyDoc(fileId) {
    const { data } = await researchClient.post('/research/classify-doc', { file_id: fileId });
    return data;
  },

  /**
   * Ask an LLM to suggest a canonical mapping between existing and new metric key names.
   * @param {string}   companyId
   * @param {string[]} existingKeys  keys already in DB
   * @param {string[]} newKeys       keys from newly extracted file
   */
  async suggestKeyRemap(companyId, existingKeys, newKeys) {
    const { data } = await researchClient.post(
      `/research/earnings/${encodeURIComponent(companyId)}/suggest-key-remap`,
      { existing_keys: existingKeys, new_keys: newKeys },
    );
    return data; // { remap: {new_key: canonical_key}, explanation: str }
  },

  /** Internal: raw POST via the authenticated research client (returns { data }). */
  async _rawPost(path, body) {
    return researchClient.post(path, body);
  },

  /**
   * Confirm a completed upload.
   *
   * By default this ingests into the KB. Set include_in_knowledge_base=false
   * to store in S3 and track the file without KB ingest/extraction.
   */
  async confirmUpload({ file_id, replace_file_id, doc_type, include_in_knowledge_base = true }) {
    const { data } = await researchClient.post('/research/upload/confirm', {
      file_id,
      replace_file_id: replace_file_id || undefined,
      doc_type: doc_type || undefined,
      include_in_knowledge_base,
    });
    return data;
  },

  /** Internal: PUT a file directly to S3 (no auth header, progress callback). */
  async _s3Put(url, file, contentType, onProgress) {
    return axios.put(url, file, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
      },
      transformRequest: [(d) => d],
    });
  },

  /** Fetch full config for a company (name, ticker, tracked_metrics). */
  async getCompanyConfig(companyId) {
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/config`,
    );
    return data; // { company_id, name, ticker, notes, tracked_metrics }
  },

  /**
   * For a given company and period_end_date, look up which of the provided
   * XBRL tags have a reported value in SEC EDGAR companyfacts.
   * @param {string} companyId
   * @param {string[]} tags  List of XBRL tag names (us-gaap)
   * @param {string} periodEndDate  ISO date (YYYY-MM-DD)
   * @returns {Promise<{[tag: string]: number|null}>}
   */
  async getSecTagValues(companyId, tags, periodEndDate) {
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/sec-tag-values`,
      { params: { tags: tags.join(','), period_end_date: periodEndDate } },
    );
    return data; // { tag: value_or_null, ... }
  },

  /**
   * Ask Bedrock to suggest alternative XBRL tags for a metric with no SEC data.
   * @param {string} companyId
   * @param {{canonical_key, row_label, period_end_date, existing_tags}} params
   * @returns {Promise<{suggestions: Array<{tag, value, rationale}>}>}
   */
  async suggestSecTags(companyId, params) {
    const { data } = await researchClient.post(
      `/research/companies/${encodeURIComponent(companyId)}/sec-tag-suggest`,
      params,
    );
    return data; // { suggestions: [{tag, value, rationale}] }
  },

  /**
   * Patch mutable company fields (name, ticker, notes, tracked_metrics).
   * @param {string} companyId
   * @param {{ name?, ticker?, notes?, tracked_metrics? }} updates
   */
  async updateCompany(companyId, updates) {
    const { data } = await researchClient.patch(
      `/research/companies/${encodeURIComponent(companyId)}`,
      updates,
    );
    return data; // updated Company
  },

  /**
   * Ask the LLM to recommend tracked metrics based on an uploaded file.
   * @param {string} companyId
   * @param {string} fileId  — already-uploaded file_id to inspect
   * @returns {{ suggested_metrics: TrackedMetric[], explanation: string }}
   */
  async suggestMetrics(companyId, fileId) {
    const { data } = await researchClient.post(
      `/research/companies/${encodeURIComponent(companyId)}/suggest-metrics`,
      { file_id: fileId },
    );
    return data;
  },

  /**
   * Re-extract tracked custom metrics from all earnings files and merge into
   * every existing earnings row for the company.  Runs synchronously server-side.
   * @param {string} companyId
   * @returns {{ success: bool, periods_updated: string[], errors: string[] }}
   */
  async backfillMetrics(companyId) {
    const { data: startData } = await researchClient.post(
      `/research/companies/${encodeURIComponent(companyId)}/backfill-metrics`,
    );
    const jobId = startData.job_id;
    // Poll until the background job finishes
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await researchClient.get(
        `/research/companies/${encodeURIComponent(companyId)}/backfill-metrics/${jobId}`,
      );
      if (data.status !== 'running') return data;
    }
  },

  /**
   * Pull structured quarterly financials from public APIs (FMP today, SEC
   * custom metrics next) and upsert them into DDB + Redshift in parallel with
   * the LLM-based PDF extraction flow.
   *
   * @param {string} companyId
   * @param {{ quarters?: number, years?: number, includeSecCustomMetrics?: boolean }} opts
   * @returns Final job-status payload once the background job finishes.
   */
  async apiBackfill(companyId, { quarters, years, includeSecCustomMetrics = false } = {}) {
    const body = { include_sec_custom_metrics: !!includeSecCustomMetrics };
    if (quarters != null) body.quarters = quarters;
    if (years != null) body.years = years;
    const { data: startData } = await researchClient.post(
      `/research/companies/${encodeURIComponent(companyId)}/api-backfill`,
      body,
    );
    const jobId = startData.job_id;
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await researchClient.get(
        `/research/companies/${encodeURIComponent(companyId)}/api-backfill/${jobId}`,
      );
      if (data.status !== 'running') return data;
    }
  },

  /**
   * Manually trigger an SEC filing poll for one company (requires
   * sec_polling_enabled + a ticker). Also runs automatically once per day
   * for every company with SEC polling turned on.
   *
   * @param {string} companyId
   * @returns {{ success, cik, filings_found, earnings_refresh_triggered, earnings_refresh_result, error }}
   */
  async pollSecFilings(companyId) {
    const { data } = await researchClient.post(
      `/research/companies/${encodeURIComponent(companyId)}/sec-poll`,
    );
    return data;
  },

  /**
   * Discover the XBRL tags SEC EDGAR has reported for this company's ticker.
   * Returned items can be used to populate the picker that lets the user add
   * custom tracked metrics with an exact `xbrl_tag` + `taxonomy`.
   *
   * @param {string} companyId
   * @param {{ q?: string, taxonomy?: string, limit?: number }} opts
   * @returns {{ ticker, total, returned, taxonomies, metrics }}
   */
  async listSecAvailableMetrics(companyId, { q, taxonomy, limit } = {}) {
    const params = {};
    if (q) params.q = q;
    if (taxonomy) params.taxonomy = taxonomy;
    if (limit != null) params.limit = limit;
    const { data } = await researchClient.get(
      `/research/companies/${encodeURIComponent(companyId)}/sec-available-metrics`,
      { params },
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Preset questions
  // ---------------------------------------------------------------------------

  /** Return all global preset questions, sorted by sort_order. */
  async listPresetQuestions() {
    const { data } = await researchClient.get('/research/preset-questions');
    return data.questions || [];
  },

  /** Create a new preset question. */
  async createPresetQuestion({ text, label, sort_order = 0 }) {
    const { data } = await researchClient.post('/research/preset-questions', { text, label, sort_order });
    return data;
  },

  /** Update an existing preset question. */
  async updatePresetQuestion(questionId, updates) {
    const { data } = await researchClient.put(
      `/research/preset-questions/${encodeURIComponent(questionId)}`,
      updates,
    );
    return data;
  },

  /** Delete a preset question. */
  async deletePresetQuestion(questionId) {
    const { data } = await researchClient.delete(
      `/research/preset-questions/${encodeURIComponent(questionId)}`,
    );
    return data;
  },

  /**
   * Download a financial model spreadsheet for a company.
   *
   * The response is a binary .xlsx blob that the browser is instructed to save.
   *
   * @param {string} companyId
   * @param {string} [template="financial_model"]  Registry key of the template to use.
   * @param {string} [definitionId="default"]      Spreadsheet definition id.
   */
  async downloadSpreadsheet(companyId, template = 'financial_model', definitionId = 'default', forecastQuarters = 0, scale = 'ones', hideEmptyRows = false) {
    const response = await researchClient.get(
      `/research/earnings/${encodeURIComponent(companyId)}/export`,
      {
        params: {
          template,
          definition_id: definitionId,
          ...(forecastQuarters > 0 && { forecast_quarters: forecastQuarters }),
          ...(scale && scale !== 'ones' && { scale }),
          ...(hideEmptyRows && { hide_empty_rows: true }),
        },
        responseType: 'blob',
      },
    );
    // Extract filename from Content-Disposition header if present
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${companyId}_financial_model.xlsx`;

    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * Fetch a financial model spreadsheet as a Blob without forcing a browser
   * download. Useful for in-app spreadsheet preview rendering.
   */
  // ---------------------------------------------------------------------------
  // Custom chart configuration
  // ---------------------------------------------------------------------------

  /**
   * List all saved custom charts, newest first.
   *
   * Charts are global (shared across companies); the leading `companyId`
   * argument is accepted for call-site compatibility but ignored.
   * @returns {Promise<Array>} List of CustomChartOut objects.
   */
  async listCharts(/* companyId */) {
    const { data } = await researchClient.get('/research/charts');
    return data;
  },

  /**
   * Save a new custom chart configuration.
   *
   * Charts are global; the leading `companyId` argument is ignored.
   * @param {string} _companyId - Ignored (kept for call-site compatibility).
   * @param {string} name - Human-readable chart name.
   * @param {Object} config - Full chart state object.
   * @returns {Promise<Object>} The created CustomChartOut record.
   */
  async createChart(_companyId, name, config) {
    const { data } = await researchClient.post('/research/charts', { name, config });
    return data;
  },

  /**
   * Update an existing saved custom chart.
   *
   * Charts are global; the leading `companyId` argument is ignored.
   * @param {string} _companyId - Ignored (kept for call-site compatibility).
   * @param {string} chartId - UUID of the chart to update.
   * @param {string} name - Updated name.
   * @param {Object} config - Updated full chart state.
   * @returns {Promise<Object>} The updated CustomChartOut record.
   */
  async updateChart(_companyId, chartId, name, config) {
    const { data } = await researchClient.put(
      `/research/charts/${encodeURIComponent(chartId)}`,
      { name, config },
    );
    return data;
  },

  /**
   * Delete a saved custom chart.
   *
   * Charts are global; the leading `companyId` argument is ignored.
   * @param {string} _companyId - Ignored (kept for call-site compatibility).
   * @param {string} chartId - UUID of the chart to delete.
   * @returns {Promise<void>}
   */
  async deleteChart(_companyId, chartId) {
    await researchClient.delete(
      `/research/charts/${encodeURIComponent(chartId)}`,
    );
  },

  async fetchSpreadsheetBlob(companyId, template = 'financial_model', definitionId = 'default', forecastQuarters = 0, scale = 'ones', hideEmptyRows = false) {
    const response = await researchClient.get(
      `/research/earnings/${encodeURIComponent(companyId)}/export`,
      {
        params: {
          template,
          definition_id: definitionId,
          ...(forecastQuarters > 0 && { forecast_quarters: forecastQuarters }),
          ...(scale && scale !== 'ones' && { scale }),
          ...(hideEmptyRows && { hide_empty_rows: true }),
        },
        responseType: 'blob',
      },
    );
    return response.data;
  },
};

export default researchService;
export { researchService };
